import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Header from '../../../components/ui/Header';


const CapabilitiesPreview = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  const capabilities = [
    {
      id: 'frontend',
      title: 'Frontend Development',
      icon: 'Monitor',
      description: 'Modern, responsive user interfaces built with cutting-edge frameworks',
      code: `// React Component with Hooks
const UserDashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const { user, loading } = useAuth();
  
  useEffect(() => {
    const fetchAnalytics = async () => {
      const data = await api.getAnalytics(user.id);
      setAnalytics(data);
    };
    
    if (user) fetchAnalytics();
  }, [user]);
  
  if (loading) return <LoadingSpinner />;
  
  return (
    <div className="dashboard-container">
      <Header user={user} />
      <MetricsGrid data={analytics} />
      <ChartSection analytics={analytics} />
    </div>
  );
};`,
      technologies: ['React', 'Vue.js', 'TypeScript', 'Tailwind CSS'],
      metrics: '50+ UI Components Built'
    },
    {
      id: 'backend',
      title: 'Backend Architecture',
      icon: 'Server',
      description: 'Scalable APIs and microservices with enterprise-grade security',
      code: `// Express.js API with Authentication
const express = require('express');
const jwt = require('jsonwebtoken');
const rateLimit = require('express-rate-limit');

const app = express();

// Security middleware
app.use(rateLimit({
  windowMs: 15 * 60 * 1000, // 15 minutes
  max: 100 // limit each IP to 100 requests per windowMs
}));

// Protected route
app.get('/api/v1/analytics', authenticateToken, async (req, res) => {
  try {
    const analytics = await AnalyticsService.getByUserId(req.user.id);
    res.json({ success: true, data: analytics });
  } catch (error) {
    res.status(500).json({ error: 'Internal server error' });
  }
});

function authenticateToken(req, res, next) {
  const token = req.headers['authorization']?.split(' ')[1];
  if (!token) return res.sendStatus(401);
  
  jwt.verify(token, process.env.JWT_SECRET, (err, user) => {
    if (err) return res.sendStatus(403);
    req.user = user;
    next();
  });
}`,
      technologies: ['Node.js', 'Python', 'PostgreSQL', 'Redis'],
      metrics: '99.9% API Uptime'
    },
    {
      id: 'cloud',
      title: 'Cloud Infrastructure',
      icon: 'Cloud',
      description: 'Scalable cloud solutions with automated deployment and monitoring',
      code: `# Docker Compose for Microservices
version: '3.8'
services:
  api-gateway:
    image: nginx:alpine
    ports:
      - "80:80"
    depends_on:
      - user-service
      - analytics-service
    volumes:
      - ./nginx.conf:/etc/nginx/nginx.conf
      
  user-service:
    build: ./services/user
    environment:
      - DATABASE_URL=postgresql://user:pass@db:5432/users
      - REDIS_URL=redis://redis:6379
    depends_on:
      - db
      - redis
      
  analytics-service:
    build: ./services/analytics
    environment:
      - MONGODB_URI=mongodb://mongo:27017/analytics
    depends_on:
      - mongo
      
  db:
    image: postgres:13
    environment:
      POSTGRES_DB: users
      POSTGRES_USER: user
      POSTGRES_PASSWORD: pass
    volumes:
      - postgres_data:/var/lib/postgresql/data
      
volumes:
  postgres_data:`,
      technologies: ['AWS', 'Docker', 'Kubernetes', 'Terraform'],
      metrics: 'Auto-scaling Infrastructure'
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const element = document.getElementById('capabilities-section');
    if (element) observer.observe(element);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTab((prev) => (prev + 1) % capabilities.length);
    }, 8000);

    return () => clearInterval(interval);
  }, []);

  return (
    <section id="capabilities-section" className="py-20 bg-muted/30">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-secondary/10 text-secondary px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Icon name="Code" size={16} />
            <span>Technical Capabilities</span>
          </div>
          
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
            Where <span className="text-gradient-brand">Code Meets Craft</span>
          </h2>
          
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            Explore our technical expertise through real code examples and architectural patterns 
            that power enterprise-grade applications.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Capability Tabs */}
          <div className={`space-y-6 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
            {capabilities.map((capability, index) => (
              <div
                key={capability.id}
                className={`p-6 rounded-brand border cursor-pointer transition-all duration-300 ${
                  activeTab === index
                    ? 'bg-secondary/5 border-secondary shadow-brand-card'
                    : 'bg-card border-border hover:border-secondary/50'
                }`}
                onClick={() => setActiveTab(index)}
              >
                <div className="flex items-start space-x-4">
                  <div className={`p-3 rounded-brand transition-colors ${
                    activeTab === index ? 'bg-secondary text-white' : 'bg-muted text-text-secondary'
                  }`}>
                    <Icon name={capability.icon} size={24} />
                  </div>
                  
                  <div className="flex-1">
                    <h3 className="text-xl font-bold mb-2">{capability.title}</h3>
                    <p className="text-text-secondary mb-4">{capability.description}</p>
                    
                    {/* Technologies */}
                    <div className="flex flex-wrap gap-2 mb-3">
                      {capability.technologies.map((tech) => (
                        <span
                          key={tech}
                          className={`px-3 py-1 rounded-full text-xs font-medium ${
                            activeTab === index
                              ? 'bg-secondary/20 text-secondary' :'bg-muted text-text-secondary'
                          }`}
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                    
                    {/* Metrics */}
                    <div className="flex items-center space-x-2">
                      <Icon name="TrendingUp" size={16} className="text-success" />
                      <span className="text-sm font-medium text-success">{capability.metrics}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Code Display */}
          <div className={`transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
            <div className="sticky top-8">
              <div className="bg-card border border-border rounded-brand shadow-brand-card overflow-hidden">
                {/* Code Editor Header */}
                <div className="flex items-center justify-between px-4 py-3 bg-muted border-b border-border">
                  <div className="flex items-center space-x-2">
                    <div className="flex space-x-1">
                      <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                      <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                      <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                    </div>
                    <span className="text-sm font-medium text-text-secondary ml-4">
                      {capabilities[activeTab].title.toLowerCase().replace(' ', '-')}.js
                    </span>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Icon name="Play" size={16} className="text-success" />
                    <span className="text-xs text-success">Live</span>
                  </div>
                </div>

                {/* Code Content */}
                <div className="p-6 bg-gray-900 text-green-400 font-mono text-sm max-h-96 overflow-y-auto">
                  <pre className="whitespace-pre-wrap">
                    {capabilities[activeTab].code}
                  </pre>
                </div>

                {/* Code Footer */}
                <div className="px-4 py-3 bg-muted border-t border-border">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Icon name={capabilities[activeTab].icon} size={16} className="text-secondary" />
                      <span className="text-sm font-medium">{capabilities[activeTab].title}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-success rounded-full animate-pulse"></div>
                      <span className="text-xs text-text-secondary">Production Ready</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center mt-16">
          <Link to="/services-deep-dive">
            <Button
              variant="default"
              size="lg"
              iconName="ArrowRight"
              iconPosition="right"
              className="cta-primary hover-lift"
            >
              Explore All Capabilities
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default CapabilitiesPreview;